import {
  defer,
  finalize,
  firstValueFrom,
  of,
  share,
  Subject,
  take,
  tap,
  type Observable,
} from "rxjs";
import type { FileInfo } from "@exagens/behavioral-insight-console-client";
import { getSparxBlueprint$ } from "@/services/internalSparxEditor.service";
import type { AppDispatch, RootState } from "@/store/app/storeTypes";
import type { SparxsBlueprintFetchResult } from "@/types/sparxsBlueprintFetchResult";
import type { SparxsBlueprintCacheEntry } from "@/store/sparxsCatalog/blueprintCacheSlice";
import { upsertSparxBlueprintCacheEntry } from "@/store/sparxsCatalog/blueprintCacheSlice";
import { shouldRefetchBlueprintFromServer } from "@/features/sparxsEditor/lib/catalog/catalogVersioning";
import { getEditableSparxCatalogFile } from "@/store/sparxsCatalog/listSelectors";

export const blueprintFetchImpl = {
  runBlueprintGet$: (
    fileName: string,
    init?: RequestInit,
  ): Observable<SparxsBlueprintFetchResult> =>
    getSparxBlueprint$(fileName, init),
};

const blueprintFetchCoalescedByFileName = new Map<
  string,
  Observable<SparxsBlueprintFetchResult>
>();

const isStalerNetworkGetThanBlueprintCache = (
  cache: SparxsBlueprintCacheEntry | undefined,
  resultLastModified: number | undefined,
): boolean => {
  if (cache == null) {
    return false;
  }
  if (cache.lastKnownServerModified == null) {
    return false;
  }
  if (resultLastModified == null) {
    return false;
  }
  return resultLastModified < cache.lastKnownServerModified;
};

export const blueprintFetchCoalesced$ = (
  fileName: string,
  init?: RequestInit,
): Observable<SparxsBlueprintFetchResult> => {
  const existing = blueprintFetchCoalescedByFileName.get(fileName);
  if (existing !== undefined) {
    return existing;
  }

  const shared = defer(() =>
    blueprintFetchImpl.runBlueprintGet$(fileName, init),
  ).pipe(
    share({
      connector: () => new Subject<SparxsBlueprintFetchResult>(),
      resetOnError: false,
      resetOnComplete: true,
      resetOnRefCountZero: false,
    }),
    finalize(() => {
      blueprintFetchCoalescedByFileName.delete(fileName);
    }),
  );

  blueprintFetchCoalescedByFileName.set(fileName, shared);
  return shared;
};

export type BlueprintFetchResolvedOptions = {
  bypassCache?: boolean;
};

const blueprintFetchResolved$ = (
  fileName: string,
  getFileInfo: () => FileInfo | undefined,
  getState: () => RootState,
  dispatch: AppDispatch,
  init: RequestInit | undefined,
  options?: BlueprintFetchResolvedOptions,
): Observable<SparxsBlueprintFetchResult> => {
  return defer(() => {
    const file = getFileInfo();
    const cache = getState().editor.sparxsBlueprintCache.byFileName[fileName];
    const bypass = options?.bypassCache === true;
    if (
      !bypass &&
      !shouldRefetchBlueprintFromServer(file, cache) &&
      cache != null
    ) {
      return of({
        blueprint: cache.blueprint,
        lastModified: cache.lastKnownServerModified,
      });
    }
    return blueprintFetchCoalesced$(fileName, init).pipe(
      tap((result) => {
        const fileAfter = getFileInfo();
        const cache =
          getState().editor.sparxsBlueprintCache.byFileName[fileName];
        if (isStalerNetworkGetThanBlueprintCache(cache, result.lastModified)) {
          return;
        }
        dispatch(
          upsertSparxBlueprintCacheEntry({
            fileName,
            blueprint: result.blueprint,
            lastKnownServerModified: result.lastModified,
            listLastModified: fileAfter?.lastModified,
          }),
        );
      }),
    );
  });
};

export type FetchBlueprintResolvedOnceParams = {
  fileName: string;
  getState: () => RootState;
  dispatch: AppDispatch;
  signal?: AbortSignal;
  options?: BlueprintFetchResolvedOptions;
};

export const fetchBlueprintResolvedOnce = async (
  params: FetchBlueprintResolvedOnceParams,
): Promise<SparxsBlueprintFetchResult> => {
  const { fileName, getState, dispatch, signal, options } = params;
  const init = signal !== undefined ? { signal } : undefined;
  const getFileInfo = () => getEditableSparxCatalogFile(getState(), fileName);
  return await firstValueFrom(
    blueprintFetchResolved$(
      fileName,
      getFileInfo,
      getState,
      dispatch,
      init,
      options,
    ).pipe(take(1)),
  );
};
